/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  executeRunIngestionAnalytics,
  fetchAnalyticsInfo,
  fetchIngestionsAnalytics,
  getIngestionData,
  getIngestionListData,
  getIngestionDetails,
  getIngestionSummary,
  getIngestionViewWizard,
  IIngestionSummaryResponse,
  postIngestionSourceFolderData,
  postIngestionSourceFolders,
  postIngestionSourceHeaders,
  postIngestionReRun,
  postIngestionMapping,
  postIngestionPreview,
  postIngestionSaveDraft,
  postIngestionRun,
  rollbackIngestion,
  deleteIngestion,
  approveIngestion,
  postIngestionErrors,
  fetchESIndexingInfo,
  fetchESIndexingList,
  fetchIndexErrorsGrid,
  exportIndexingErrorData,
  runESIndexing,
  fetchIngestionSearchTreeViewData,
  fetchUnpublishHistoryData,
  unpublishIngestion,
  exportUnpublishedDocData,
  postIngestionErrorsupdate,
  postIngestionIgnoreAll,
  fetchExportIngestionErrors,
  getIngestionDataSets,
  getICEDataSetTitlePage,
  getICELoadDatasetView,
  postIgnoreAllIngestions,
  postIngestionCopy,
  postIngestionEdit,
  postIngestionCopying,
  getApoList,
  ApoRefreshSubmit,
  getApoProgress,
} from '../../api/endpoints/ingestionApi';
import {
  IngestionListResponse,
  IngestionListRequest,
  IngestionDetailsRequest,
  IngestionDetailsType,
  IIngestionViewWizard,
  IFoldersData,
  IIngestionHeaderResponse,
  IIngestionMappingPayload,
  IIngestionPreviewPayload,
  IIngestionPreviewResponse,
  IIngestionSaveDraftPayload,
  IIngestionSaveDraftResponse,
  IMappingResponse,
  IRunIngestionResponse,
  RollbackIngestionRequest,
  PaginationReqPayloadType,
  IndexErrorsGridReqPayloadType,
  RunESIndexingPayloadType,
  IngestionErrorsModelResponse,
  IngestionErrorsModelPayload,
  IngestionErrorsUpdateModelResponse,
  IngestionErrorsUpdateModelpayLoad,
  IngestionErrorsExportPayload,
  IngestionErrorsIgnoreAllPayload,
  IngestionErrorsIgnoreAllResponse,
  IngestionUnpublishHistoryReqPayload,
  ICEDataSetTilePageResponse,
  IngestionErrorsIgnoreAllRequestPayload,
  RunAnalyticsPayloadType,
  IIngestionCopyResponse,
  IIngestionCopyRequest,
  IApoListResponse,
  IApoListRequest,
  IApoProgressApiResponse,
} from '../../api/types/ingestionApi.type';

export const useGetIngestionData = (Key: number) => {
  return useQuery({
    queryKey: ['ingestionView'],
    queryFn: async () => {
      return getIngestionData();
    },
  });
};

export const useGetIngestionViewWizard = (projectId: number) => {
  return useQuery<IIngestionViewWizard, Error>({
    queryKey: ['ingestion', 'viewWizard', projectId],
    queryFn: getIngestionViewWizard,
    staleTime: 1000 * 60 * 10,
  });
};
//Ingestion Errors start

export const usePostIngestionErrors = (
  onSuccess?: (data: IngestionErrorsModelResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IngestionErrorsModelResponse,
    Error,
    IngestionErrorsModelPayload
  >({
    mutationFn: postIngestionErrors,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const usePostIgnoreAllBtnErrors = (
  onSuccess?: (data: IngestionErrorsIgnoreAllResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IngestionErrorsIgnoreAllResponse,
    Error,
    IngestionErrorsIgnoreAllRequestPayload
  >({
    mutationFn: postIgnoreAllIngestions,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const usePostIgnoreAllErrors = (
  payload: IngestionErrorsIgnoreAllPayload
) => {
  return useQuery<IngestionErrorsIgnoreAllResponse, Error>({
    queryKey: [payload],
    queryFn: () => postIngestionIgnoreAll(payload),
    retry: 2,
  });
};

export const useUpdateIngestionErrors = (
  onSuccess?: (data: IngestionErrorsUpdateModelResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IngestionErrorsUpdateModelResponse,
    Error,
    IngestionErrorsUpdateModelpayLoad
  >({
    mutationFn: postIngestionErrorsupdate,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useExportIngestionErrors = (
  payload: IngestionErrorsExportPayload | null
) => {
  return useQuery({
    queryKey: ['ingestionErrorsExport', payload],
    queryFn: async () => {
      if (!payload) return null;
      return fetchExportIngestionErrors(payload);
    },
    enabled: !!payload,
    staleTime: 1000 * 60 * 10,
  });
};

//end Ingestion Errors

export const useGetIngestionListData = (queryParams: IngestionListRequest) => {
  return useQuery<IngestionListResponse, Error>({
    queryKey: ['ingestionListView', queryParams],
    queryFn: async () => {
      return getIngestionListData(queryParams);
    },
    retry: 2,
  });
};

export const useGetIngestionDetails = (
  queryParams: IngestionDetailsRequest
) => {
  return useQuery<IngestionDetailsType, Error>({
    queryKey: ['ingestionDetailsView', queryParams],
    queryFn: async () => {
      return getIngestionDetails(queryParams);
    },
    retry: 2,
    enabled: !!queryParams.projectId && !!queryParams.ingestionId,
  });
};

export const usePostIngestionReRun = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: postIngestionReRun,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
export const usePostIngestionCoping = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: postIngestionCopying,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useIngestionSummary = (options?: {enabled?: boolean}) => {
  return useQuery<IIngestionSummaryResponse, Error>({
    queryKey: ['ingestionSummary'],
    queryFn: getIngestionSummary,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export const useGetIngestionSourceFolders = (
  serverLocation: {key: string; displayValue: string} | null,
  lstServerLocation: {value: string; text: string}[] = [],
  options?: UseQueryOptions<any, Error>
) => {
  return useQuery<any, Error>({
    queryKey: ['ingestion', 'sourceFolders', serverLocation?.key ?? null],
    queryFn: async () => {
      const payload = {
        serverLocation: String(serverLocation?.key ?? ''),
        serverLocationDetails:
          lstServerLocation.length > 0 ? lstServerLocation : [],
      };

      const resp = await postIngestionSourceFolders(payload);
      resp.objFolder.sort(
        (
          a: {
            isSelected: boolean;
            key: string;
            text: string | null;
            value: string;
          },
          b: {
            isSelected: boolean;
            key: string;
            text: string | null;
            value: string;
          }
        ) => {
          if (a.value < b.value) return -1;
          if (a.value > b.value) return 1;
          return 0;
        }
      );
      return resp?.objFolder ?? [];
    },
    enabled: !!serverLocation,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useGetIngestionSourceFoldersData = (
  serverLocation: {key: string; displayValue: string} | null,
  lstServerLocation: {value: string; text: string}[] = [],
  folder: {key: string; displayValue: string} | null,
  options?: UseQueryOptions<any, Error>
) => {
  return useQuery<IFoldersData, Error>({
    queryKey: [
      'ingestion',
      'sourceFoldersData',
      serverLocation?.key ?? null,
      folder?.key ?? null,
    ],
    queryFn: async () => {
      const payload = {
        serverLocation: String(serverLocation?.key ?? ''),
        folderName: String(folder?.displayValue ?? folder?.key ?? ''),
        serverLocationDetails:
          lstServerLocation.length > 0 ? lstServerLocation : [],
      };

      const resp = await postIngestionSourceFolderData(payload);
      return resp ?? null;
    },
    // only run when a folder is actually selected
    enabled: !!serverLocation && !!folder,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useGetIngestionSourceHeaders = (
  serverLocation: {key: string; displayValue: string} | null,
  rawLstServerLocation: {value: string; text: string}[] = [],
  folder: {key: string; displayValue: string} | null,
  columnDelimiterId?: string | null,
  textQualifierId?: string | null,
  dataDelimiterId?: string | null,
  datFile?: string | null,
  options?: UseQueryOptions<any, Error>
) => {
  const enabled =
    !!serverLocation &&
    !!folder &&
    !!columnDelimiterId &&
    !!textQualifierId &&
    !!dataDelimiterId &&
    !!datFile;

  return useQuery<IIngestionHeaderResponse, Error>({
    queryKey: [
      'ingestion',
      'sourceHeaders',
      serverLocation?.key ?? null,
      folder?.key ?? null,
      columnDelimiterId ?? null,
      textQualifierId ?? null,
      dataDelimiterId ?? null,
      datFile ?? null,
    ],
    queryFn: async () => {
      const payload = {
        serverLocationValue: String(serverLocation?.key ?? ''),
        folderName: String(folder?.displayValue ?? folder?.key ?? ''),
        columnDelimiterId: String(columnDelimiterId ?? ''),
        textQualifierId: String(textQualifierId ?? ''),
        dataDelimiterId: String(dataDelimiterId ?? ''),
        datFile: String(datFile ?? ''),
        serverLocationDetails:
          rawLstServerLocation.length > 0 ? rawLstServerLocation : [],
      };

      const resp = await postIngestionSourceHeaders(payload);
      return resp ?? null;
    },
    enabled,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useAnalyticsData = (payload: any) => {
  return useQuery({
    queryKey: ['analyticsView'],
    queryFn: async () => {
      return fetchIngestionsAnalytics(payload);
    },
    staleTime: 1000 * 60 * 10,
  });
};
export const useAnalyticsInfo = () => {
  return useQuery({
    queryKey: ['analyticsInfo'],
    queryFn: async () => {
      return fetchAnalyticsInfo();
    },
    staleTime: 1000 * 60 * 10,
  });
};

export const useRunIngestionAnalytics = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<object, Error, RunAnalyticsPayloadType>({
    mutationFn: executeRunIngestionAnalytics,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['analyticsIndexingData', 'analyticsIndexingConfigInfo'],
      });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const usePostIngestionMapping = (
  onSuccess?: (data: IMappingResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IMappingResponse, Error, IIngestionMappingPayload>({
    mutationFn: postIngestionMapping,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRollbackIngestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RollbackIngestionRequest) =>
      rollbackIngestion(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['ingestionListView']});
    },
  });
};

export const usePostIngestionPreview = (
  onSuccess?: (data: IIngestionPreviewResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IIngestionPreviewResponse,
    Error,
    IIngestionPreviewPayload
  >({
    mutationFn: postIngestionPreview,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const usePostIngestionSaveDraft = (
  onSuccess?: (data: IIngestionSaveDraftResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IIngestionSaveDraftResponse,
    Error,
    IIngestionSaveDraftPayload
  >({
    mutationFn: postIngestionSaveDraft,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const usePostIngestionRun = (
  onSuccess?: (data: IRunIngestionResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IRunIngestionResponse, Error, number>({
    mutationFn: postIngestionRun,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeleteIngestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: RollbackIngestionRequest) => deleteIngestion(params),

    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['ingestionListView']});
    },
  });
};

export const useApproveIngestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: RollbackIngestionRequest) => approveIngestion(params),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['ingestionListView']});
    },
  });
};

export const useESIndexingConfigInfo = () => {
  return useQuery({
    queryKey: ['esIndexingConfigInfo'],
    queryFn: async () => {
      return fetchESIndexingInfo();
    },
    staleTime: 1000 * 60 * 10,
  });
};

export const useESIndexingListData = (payload: PaginationReqPayloadType) => {
  return useQuery({
    queryKey: ['esIndexingData', payload],
    queryFn: async () => {
      return fetchESIndexingList(payload);
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 0,
  });
};

export const useIndexErrorsGridData = (
  payload: IndexErrorsGridReqPayloadType
) => {
  return useQuery({
    queryKey: ['indexErrorGrid', payload],
    queryFn: async () => fetchIndexErrorsGrid(payload),
    staleTime: 1000 * 60 * 10,
  });
};

export const useExportIndexingErrorData = (payload: {analyticsId: number}) => {
  return useQuery({
    queryKey: ['export-indexing-errors'],
    queryFn: async () => exportIndexingErrorData(payload),
    enabled: false,
    staleTime: Infinity,
    retry: false,
    retryOnMount: false,
    gcTime: 0, // Set zero for clear cache on success of expoert req submit
  });
};

export const useRunESIndexing = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<object, Error, RunESIndexingPayloadType>({
    mutationFn: runESIndexing,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['esIndexingData', 'esIndexingConfigInfo'],
      });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUnpublishSearchTreeData = () => {
  return useQuery({
    queryKey: ['unpublish-search-tree-data'],
    queryFn: async () => fetchIngestionSearchTreeViewData(),
    staleTime: 1000 * 60 * 10,
    gcTime: 0,
  });
};

export const useUnpublishHistoryData = (
  payload: IngestionUnpublishHistoryReqPayload
) => {
  return useQuery({
    queryKey: ['unpublish-history-data', payload],
    queryFn: async () => fetchUnpublishHistoryData(payload),
    staleTime: 1000 * 60 * 10,
    gcTime: 0,
  });
};

export const useUnpublishIngestion = (
  onSuccess?: (data: unknown) => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {analyzingSearchId: string}) =>
      unpublishIngestion(payload),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ['unpublish-search-tree-data', 'unpublish-history-data'],
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useIngestionExportUnpublishDocData = (
  onSuccess?: (data: {message: string}) => void,
  onError?: (err: Error) => void
) => {
  return useMutation({
    mutationFn: (payload: {analyzingSearchId: string}) =>
      exportUnpublishedDocData(payload),
    onSuccess: async (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useIngestionDataSets = () => {
  return useQuery({
    queryKey: ['ingestion-datasets'],
    queryFn: async () => getIngestionDataSets(),
    staleTime: 1000 * 60 * 10,
    gcTime: 0,
  });
};

export const useICEDatasetTilePage = (
  onSuccess?: (data: ICEDataSetTilePageResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation({
    mutationFn: getICEDataSetTitlePage,
    onSuccess: async (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useICELoadDatasetView = (
  onSuccess?: (data: {totalDatasetCount: number}) => void,
  onError?: (err: Error) => void
) => {
  return useMutation({
    mutationFn: getICELoadDatasetView,
    onSuccess: async (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const usePostIngestionCopy = (
  onSuccess?: (data: IIngestionCopyResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IIngestionCopyResponse, Error, IIngestionCopyRequest>({
    mutationFn: postIngestionCopy,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const usePostIngestionEdit = (
  onSuccess?: (data: IIngestionCopyResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IIngestionCopyResponse, Error, IIngestionCopyRequest>({
    mutationFn: postIngestionEdit,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useApoList = (payload: IApoListRequest) => {
  return useQuery<IApoListResponse, Error>({
    queryKey: ['apoList', payload],
    queryFn: () => getApoList(payload),
    retry: 2,
  });
};

export const useApoRefresh = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    boolean,
    Error
    
  >({
    mutationFn: () =>
      ApoRefreshSubmit(),
    mutationKey: ['ApoRefreshSubmit'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

const APO_PROGRESS_POLL_INTERVAL_MS = 10_000;

export const useApoProgress = () => {
  return useQuery<IApoProgressApiResponse, Error>({
    queryKey: ['apoProgress'],
    queryFn: () => getApoProgress(),
    retry: 2,
    refetchInterval: (query) => {
      const data = query.state.data;
      const isInProgress =
        data?.showProgress === true &&
        /inprogress/i.test(String(data?.statusCode ?? ''));
      return isInProgress ? APO_PROGRESS_POLL_INTERVAL_MS : false;
    },
  });
};
